import React, { useMemo } from "react";
import { Card } from "primereact/card";
import { Chart } from "primereact/chart";
import type { AnomalyWeekly } from "../../../types/kpiDashboard";

interface Props {
  anomalyTrend:  AnomalyWeekly[];
  invoiceTrend:  { week: string; amount: number }[];
}

const baseOptions = {
  responsive: true,
  maintainAspectRatio: false,
  animation: { duration: 0 },
  plugins: {
    legend: {
      labels: { color: "#6c757d", font: { size: 12 } },
      position: "top" as const,
    },
  },
  scales: {
    x: { ticks: { color: "#6c757d", font: { size: 11 } }, grid: { color: "#f3f4f6" } },
    y: { ticks: { color: "#6c757d", font: { size: 11 } }, grid: { color: "#f3f4f6" } },
  },
};

const AnomalyAndInvoiceCharts: React.FC<Props> = ({ anomalyTrend, invoiceTrend }) => {
  const anomalyChartData = useMemo(() => ({
    labels: anomalyTrend.map((d) => d.week),
    datasets: [
      {
        label: "Partner Leer",
        data: anomalyTrend.map((d) => d.partnerLeer),
        backgroundColor: "#f87171",
        borderRadius: 4,
        stack: "anomalies",
      },
      {
        label: "No Consumption",
        data: anomalyTrend.map((d) => d.noConsumption),
        backgroundColor: "#fb923c",
        borderRadius: 4,
        stack: "anomalies",
      },
    ],
  }), [anomalyTrend]);

  const invoiceChartData = useMemo(() => ({
    labels: invoiceTrend.map((d) => d.week),
    datasets: [
      {
        label: "Overdue Amount (€)",
        data: invoiceTrend.map((d) => d.amount),
        backgroundColor: "rgba(239,68,68,0.7)",
        borderColor: "#ef4444",
        borderWidth: 1,
        borderRadius: 4,
      },
    ],
  }), [invoiceTrend]);

  const stackedOptions = {
    ...baseOptions,
    scales: {
      ...baseOptions.scales,
      x: { ...baseOptions.scales.x, stacked: true },
      y: { ...baseOptions.scales.y, stacked: true },
    },
  };

  const invoiceOptions = {
    ...baseOptions,
    plugins: {
      ...baseOptions.plugins,
      tooltip: {
        callbacks: {
          label: (ctx: any) =>
            `€${Number(ctx.raw).toLocaleString("de-DE", { minimumFractionDigits: 2 })}`,
        },
      },
    },
  };

  const chartStyle = { height: "220px" };

  return (
    <div className="grid mt-0">
      <div className="col-12 md:col-6">
        <Card title="CDR Anomalies by Week">
          <Chart type="bar" data={anomalyChartData} options={stackedOptions} style={chartStyle} />
        </Card>
      </div>
      <div className="col-12 md:col-6">
        <Card title="Overdue Invoice Amount (€) by Week">
          <Chart type="bar" data={invoiceChartData} options={invoiceOptions} style={chartStyle} />
        </Card>
      </div>
    </div>
  );
};

export default AnomalyAndInvoiceCharts;
